import { FILLING_RATE_OBJECTIVE } from "../utils/collectionInsights";

const SIX_MONTH_SHIFT_THRESHOLD = 5;
const MAX_PHOTOS_PER_FLUX = 6;
const MAX_WEEKLY_PERIOD_MS = 8 * 24 * 60 * 60 * 1000;

const asArray = (value) => (Array.isArray(value) ? value : []);

export const resolveWeeklyPeriod = (periodStart, periodStop) => {
  const start = new Date(periodStart);
  const stop = new Date(periodStop);
  if (Number.isNaN(start.getTime()) || Number.isNaN(stop.getTime())) {
    return { start: periodStart, stop: periodStop };
  }
  if (stop.getTime() - start.getTime() <= MAX_WEEKLY_PERIOD_MS) {
    return { start: start.toISOString(), stop: stop.toISOString() };
  }
  const weekStart = new Date(stop.getTime() - 7 * 24 * 60 * 60 * 1000);
  return { start: weekStart.toISOString(), stop: stop.toISOString() };
};

export const canExportWeeklyReport = ({
  selectedSiteKey,
  siteOptions = [],
  numberOfSite,
} = {}) => {
  if (selectedSiteKey) return { ok: true };
  if (siteOptions.length === 1) return { ok: true };
  if (siteOptions.length === 0 && (numberOfSite == null || numberOfSite <= 1)) {
    return { ok: true };
  }
  return { ok: false, reason: "multiple-sites" };
};

export const getSixMonthBaselineRange = (periodStop) => {
  const stop = new Date(periodStop);
  if (Number.isNaN(stop.getTime())) {
    return { start: null, stop: null };
  }
  const start = new Date(stop);
  start.setUTCMonth(start.getUTCMonth() - 6);
  return { start: start.toISOString(), stop: stop.toISOString() };
};

export const filterCollectionsByPeriod = (
  collectionsData,
  periodStart,
  periodStop,
) => {
  const startMs = new Date(periodStart).getTime();
  const stopMs = new Date(periodStop).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(stopMs)) return [];
  return asArray(collectionsData).filter((row) => {
    const time = new Date(row?.datetime).getTime();
    return !Number.isNaN(time) && time >= startMs && time <= stopMs;
  });
};

export const groupRotationsByFlux = (collectionsData = []) => {
  const byFlux = {};
  asArray(collectionsData).forEach((row) => {
    const flux =
      row?.wasteCategoryLabel?.trim?.() || row?.wasteName?.trim?.() || "";
    if (!flux) return;
    if (!byFlux[flux]) {
      byFlux[flux] = { flux, count: 0, dates: [], fillingLevels: [] };
    }
    byFlux[flux].count += 1;
    if (row.datetime) byFlux[flux].dates.push(row.datetime);
    if (typeof row.filling_level === "number") {
      byFlux[flux].fillingLevels.push(row.filling_level);
    }
  });
  return Object.values(byFlux).sort((a, b) => b.count - a.count);
};

export const groupRotationsByDayAndFlux = (collectionsData = []) => {
  const days = {};
  asArray(collectionsData).forEach((row) => {
    const flux =
      row?.wasteCategoryLabel?.trim?.() || row?.wasteName?.trim?.() || "";
    if (!flux || !row.datetime) return;
    const day = new Date(row.datetime).toISOString().slice(0, 10);
    if (Number.isNaN(new Date(day).getTime())) return;
    if (!days[day]) days[day] = {};
    days[day][flux] = (days[day][flux] || 0) + 1;
  });
  const labels = Object.keys(days).sort();
  const fluxes = [
    ...new Set(labels.flatMap((day) => Object.keys(days[day] || {}))),
  ];
  return { labels, fluxes, days };
};

export const fillingStatsFromDataResult = (dataResult = {}) => {
  if (!dataResult || typeof dataResult !== "object") return [];
  return Object.entries(dataResult)
    .map(([flux, data]) => {
      const count = data?.countFillingLevel || 0;
      return {
        flux,
        min: Number(data?.min) || 0,
        max: Number(data?.max) || 0,
        avg: count > 0 ? data.sumFillingLevel / count : 0,
        count,
      };
    })
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);
};

export const fillingStatsFromCollections = (collectionsData = []) => {
  const byFlux = {};
  asArray(collectionsData).forEach((row) => {
    const flux =
      row?.wasteCategoryLabel?.trim?.() || row?.wasteName?.trim?.() || "";
    if (!flux || typeof row.filling_level !== "number") return;
    if (!byFlux[flux]) {
      byFlux[flux] = {
        flux,
        min: row.filling_level,
        max: row.filling_level,
        sumFillingLevel: 0,
        count: 0,
      };
    }
    const stats = byFlux[flux];
    stats.min = Math.min(stats.min, row.filling_level);
    stats.max = Math.max(stats.max, row.filling_level);
    stats.sumFillingLevel += row.filling_level;
    stats.count += 1;
  });
  return Object.values(byFlux)
    .map((item) => ({
      flux: item.flux,
      min: item.min,
      max: item.max,
      avg: item.count > 0 ? item.sumFillingLevel / item.count : 0,
      count: item.count,
    }))
    .sort((a, b) => b.count - a.count);
};

export const compositionByFlux = (monthlyCaracterisation = []) => {
  if (!Array.isArray(monthlyCaracterisation)) return [];
  const byFlux = {};
  monthlyCaracterisation.forEach((entry) => {
    const flux = entry?.label;
    if (!flux) return;
    if (!byFlux[flux]) byFlux[flux] = {};
    Object.entries(entry.wasteData || {}).forEach(([material, count]) => {
      if (material === "unknown" || !count) return;
      byFlux[flux][material] = (byFlux[flux][material] || 0) + count;
    });
  });
  return Object.entries(byFlux)
    .map(([flux, counts]) => {
      const total = Object.values(counts).reduce(
        (sum, value) => sum + value,
        0,
      );
      const slices = Object.entries(counts)
        .map(([material, count]) => ({
          material,
          count,
          percent: total > 0 ? (count / total) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count);
      return { flux, total, slices };
    })
    .filter((item) => item.total > 0);
};

/** Top (maxItems-1) + « Autres » pour que les % affichés totalisent ~100 %. */
export const slicesWithOthers = (
  slices = [],
  maxItems = 12,
  othersKey = "Other",
) => {
  const positive = (Array.isArray(slices) ? slices : []).filter(
    (slice) => slice.count > 0,
  );
  if (positive.length <= maxItems) return positive;
  const head = positive.slice(0, maxItems - 1);
  const restCount = positive
    .slice(maxItems - 1)
    .reduce((sum, slice) => sum + slice.count, 0);
  const total = positive.reduce((sum, slice) => sum + slice.count, 0);
  return [
    ...head,
    {
      material: othersKey,
      count: restCount,
      percent: total > 0 ? (restCount / total) * 100 : 0,
    },
  ];
};

export const buildCompositionComparisons = (
  weekComps = [],
  baselineComps = [],
  threshold = SIX_MONTH_SHIFT_THRESHOLD,
) => {
  const baselineByFlux = Object.fromEntries(
    baselineComps.map((item) => [item.flux, item]),
  );
  return weekComps.map((week) => {
    const baseline = baselineByFlux[week.flux];
    const baselineByMaterial = Object.fromEntries(
      (baseline?.slices || []).map((slice) => [slice.material, slice.percent]),
    );
    const shifts = week.slices
      .map((slice) => {
        const baselinePercent = baselineByMaterial[slice.material];
        if (baselinePercent == null) return null;
        const delta = slice.percent - baselinePercent;
        if (Math.abs(delta) < threshold) return null;
        return {
          material: slice.material,
          weekPercent: slice.percent,
          baselinePercent,
          delta,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    return {
      flux: week.flux,
      top: week.slices.slice(0, 2),
      shifts,
    };
  });
};

const shouldShowSortingErrorPercents = (total) => total > 0;

export const sortingErrorsByFlux = (dataSortingError = {}) => {
  if (!dataSortingError || typeof dataSortingError !== "object") return [];
  const byFlux = {};
  Object.entries(dataSortingError).forEach(([material, entries]) => {
    if (material === "unknown" || !Array.isArray(entries)) return;
    entries.forEach((entry) => {
      const flux = entry?.flux;
      const count = Number(entry?.countAnomaly) || 0;
      if (!flux || count <= 0) return;
      if (!byFlux[flux]) byFlux[flux] = {};
      byFlux[flux][material] = (byFlux[flux][material] || 0) + count;
    });
  });
  return Object.entries(byFlux)
    .map(([flux, counts]) => {
      const total = Object.values(counts).reduce(
        (sum, value) => sum + value,
        0,
      );
      const slices = Object.entries(counts)
        .map(([material, count]) => ({
          material,
          count,
          percent: total > 0 ? (count / total) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count);
      return {
        flux,
        total,
        slices,
        showPercents: shouldShowSortingErrorPercents(total),
      };
    })
    .filter((item) => item.total > 0)
    .sort((a, b) => b.total - a.total);
};

export const allFillingRatesGood = (
  fillingStats = [],
  objective = FILLING_RATE_OBJECTIVE,
) =>
  fillingStats.length > 0 &&
  fillingStats.every((item) => item.avg >= objective);

/**
 * Jusqu’à maxPerFlux photos avec anomalies par flux (6 par défaut = grille 2×3).
 * Critères : plus d’anomalies d’abord, jours distincts en priorité, puis chrono.
 * Une même image (URL sans query) n’est jamais prise deux fois.
 * La page « featured » reclasse ensuite par anomalies (indépendamment du chrono).
 */
const photoIdentity = (row) => {
  const url = String(row?.image_url || "")
    .split("?")[0]
    .trim();
  if (url) return `url:${url}`;
  const id = row?._measurement || row?._id || "";
  const time = row?._time || "";
  return `id:${id}:${time}`;
};

export const selectPhotosByFlux = (
  historyList = [],
  maxPerFlux = MAX_PHOTOS_PER_FLUX,
) => {
  const withErrors = (Array.isArray(historyList) ? historyList : []).filter(
    (row) => (Number(row?.anomalies) || 0) > 0 && row?.image_url,
  );
  const byFlux = {};
  withErrors.forEach((row) => {
    const flux = row?.wasteName?.trim?.() || "";
    if (!flux) return;
    if (!byFlux[flux]) byFlux[flux] = [];
    byFlux[flux].push(row);
  });

  const result = {};
  Object.entries(byFlux).forEach(([flux, rows]) => {
    const sorted = [...rows].sort((a, b) => {
      const anomalyDiff =
        (Number(b.anomalies) || 0) - (Number(a.anomalies) || 0);
      if (anomalyDiff !== 0) return anomalyDiff;
      return new Date(a._time) - new Date(b._time);
    });
    const picked = [];
    const usedDays = new Set();
    const usedIds = new Set();
    const tryPick = (row, requireNewDay) => {
      if (picked.length >= maxPerFlux) return;
      const identity = photoIdentity(row);
      if (usedIds.has(identity)) return;
      const day = new Date(row._time).toISOString().slice(0, 10);
      if (requireNewDay && usedDays.has(day)) return;
      usedIds.add(identity);
      usedDays.add(day);
      picked.push(row);
    };
    sorted.forEach((row) => tryPick(row, true));
    sorted.forEach((row) => tryPick(row, false));
    picked.sort((a, b) => new Date(a._time) - new Date(b._time));
    result[flux] = picked;
  });
  return result;
};

export const materialsFromAnomalies = (wasteObjectsList = []) => {
  const counts = {};
  (Array.isArray(wasteObjectsList) ? wasteObjectsList : []).forEach(
    (object) => {
      const cls = object?.cls;
      if (!cls || object.isAuthorizedMaterial) return;
      const key = String(cls).toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    },
  );
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([material, count]) => ({ material, count }));
};

const anomalyBoundingArea = (object) => {
  if (!Array.isArray(object?.points_konva) || object.points_konva.length < 2) {
    if (
      Number.isFinite(object?.w_box) &&
      Number.isFinite(object?.h_box) &&
      object.w_box > 0 &&
      object.h_box > 0
    ) {
      return object.w_box * object.h_box;
    }
    return 0;
  }
  const points = object.points_konva
    .flat()
    .filter((value) => Number.isFinite(value));
  let minX;
  let minY;
  let maxX;
  let maxY;
  for (let index = 0; index < points.length - 1; index += 2) {
    const x = points[index];
    const y = points[index + 1];
    minX = minX == null ? x : Math.min(minX, x);
    minY = minY == null ? y : Math.min(minY, y);
    maxX = maxX == null ? x : Math.max(maxX, x);
    maxY = maxY == null ? y : Math.max(maxY, y);
  }
  if (minX == null || minY == null || maxX == null || maxY == null) return 0;
  return Math.max(0, maxX - minX) * Math.max(0, maxY - minY);
};

/** Jusqu’à 3 indésirables exemples : matières distinctes, plus grandes surfaces d’abord. */
export const selectExampleAnomalies = (wasteObjectsList = [], max = 3) => {
  const candidates = (Array.isArray(wasteObjectsList) ? wasteObjectsList : [])
    .filter(
      (object) =>
        object?.cls &&
        !object.isAuthorizedMaterial &&
        ((Array.isArray(object.points_konva) &&
          object.points_konva.length > 1) ||
          (Number(object.w_box) > 0 && Number(object.h_box) > 0)),
    )
    .map((object) => ({
      object,
      material: String(object.cls).toLowerCase(),
      area: anomalyBoundingArea(object),
    }))
    .sort((a, b) => b.area - a.area);

  const picked = [];
  const usedMaterials = new Set();
  candidates.forEach((item) => {
    if (picked.length >= max) return;
    if (usedMaterials.has(item.material)) return;
    usedMaterials.add(item.material);
    picked.push(item.object);
  });
  candidates.forEach((item) => {
    if (picked.length >= max) return;
    if (picked.includes(item.object)) return;
    picked.push(item.object);
  });
  return picked;
};

export const getZoomBox = (object, imageDimensions, padding = 48) => {
  if (!object || !imageDimensions?.width || !imageDimensions?.height) {
    return null;
  }
  let minX;
  let minY;
  let maxX;
  let maxY;
  if (Array.isArray(object.points_konva) && object.points_konva.length > 1) {
    const points = object.points_konva
      .flat()
      .filter((value) => Number.isFinite(value));
    for (let index = 0; index < points.length - 1; index += 2) {
      const x = points[index];
      const y = points[index + 1];
      minX = minX == null ? x : Math.min(minX, x);
      minY = minY == null ? y : Math.min(minY, y);
      maxX = maxX == null ? x : Math.max(maxX, x);
      maxY = maxY == null ? y : Math.max(maxY, y);
    }
  } else if (
    Number.isFinite(object.x_box) &&
    Number.isFinite(object.y_box) &&
    Number.isFinite(object.w_box) &&
    Number.isFinite(object.h_box)
  ) {
    minX = object.x_box;
    minY = object.y_box;
    maxX = object.x_box + object.w_box;
    maxY = object.y_box + object.h_box;
  }
  if (minX == null || minY == null || maxX == null || maxY == null) {
    return null;
  }
  const objectWidth = Math.max(1, maxX - minX);
  const objectHeight = Math.max(1, maxY - minY);
  const padX = Math.min(padding, Math.max(12, objectWidth * 0.35));
  const padY = Math.min(padding, Math.max(12, objectHeight * 0.35));
  const x = Math.max(0, minX - padX);
  const y = Math.max(0, minY - padY);
  const w = Math.min(imageDimensions.width - x, objectWidth + padX * 2);
  const h = Math.min(imageDimensions.height - y, objectHeight + padY * 2);
  if (w <= 8 || h <= 8) return null;
  return { x_box: x, y_box: y, w_box: w, h_box: h };
};

export const roundPercent = (value) => Math.round(Number(value) || 0);

export const buildWeeklyReportModel = ({
  collectionsData = [],
  dataResult = {},
  baselineDataResult = {},
  monthlyCaracterisation = [],
  baselineMonthlyCaracterisation = [],
  dataSortingError = {},
  baselineDataSortingError = {},
} = {}) => {
  const rotationsByFlux = groupRotationsByFlux(collectionsData);
  const rotationsByDay = groupRotationsByDayAndFlux(collectionsData);
  const fillingFromCollections = fillingStatsFromCollections(collectionsData);
  const fillingStats =
    fillingFromCollections.length > 0
      ? fillingFromCollections
      : fillingStatsFromDataResult(dataResult);
  const baselineFillingStats = fillingStatsFromDataResult(baselineDataResult);
  const compositions = compositionByFlux(monthlyCaracterisation);
  const baselineCompositions = compositionByFlux(
    baselineMonthlyCaracterisation,
  );
  const compositionComparisons = buildCompositionComparisons(
    compositions,
    baselineCompositions,
  );
  const sortingErrors = sortingErrorsByFlux(dataSortingError);
  const baselineSortingErrors = sortingErrorsByFlux(baselineDataSortingError);
  const sortingErrorComparisons = buildCompositionComparisons(
    sortingErrors,
    baselineSortingErrors,
  );
  const baselineFillingByFlux = Object.fromEntries(
    baselineFillingStats.map((item) => [item.flux, item]),
  );

  return {
    rotationsByFlux,
    rotationsByDay,
    fillingStats,
    baselineFillingStats,
    compositions,
    compositionComparisons,
    sortingErrors,
    sortingErrorComparisons,
    fillingGood: allFillingRatesGood(fillingStats),
    totalRotations: rotationsByFlux.reduce((sum, item) => sum + item.count, 0),
    fillingVsBaseline: fillingStats.map((item) => ({
      flux: item.flux,
      weekAvg: item.avg,
      baselineAvg: baselineFillingByFlux[item.flux]?.avg ?? null,
    })),
  };
};

/**
 * Flux exemplaire : aucune erreur de tri comptabilisée et taux
 * d’indésirables mesuré à 0 % (volume présent). Évite de féliciter
 * sur des données absentes (taux null).
 */
export const isExemplarySortingFlux = ({
  sortingErrors,
  contaminationRate,
} = {}) => contaminationRate === 0 && !(Number(sortingErrors?.total) > 0);

/**
 * Taux volumique global d’indésirables (%) :
 * Σ volume erreurs de tri / Σ volume déchets × 100 (arrondi).
 * Même base que Qualité Bilans / starsCalculation.
 */
export const computeUndesirableRate = (dataVolume = {}) => {
  let errorVol = 0;
  let wasteVol = 0;
  Object.values(dataVolume || {}).forEach((entry) => {
    errorVol += Number(entry?.totalVolumeSortingError) || 0;
    wasteVol += Number(entry?.totalWasteVolume) || 0;
  });
  if (wasteVol <= 0) return null;
  return Math.round((errorVol * 100) / wasteVol);
};
